import PropTypes from 'prop-types';
import { useState } from 'react';
import { Paper, Stack, Typography, Box, Divider, IconButton, Collapse } from '@mui/material';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';

const formatTimestamp = (ts) => {
    if (!ts) return '';
    try {
        return new Date(ts).toLocaleString();
    } catch (e) {
        return '';
    }
};

const FileUploadHistoryList = ({ entries = [], onSelectRecord = undefined }) => {
    const [expandedKey, setExpandedKey] = useState(null);

    if (!entries.length) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px dashed #d4d8e4',
                    backgroundColor: '#fafbff',
                }}
            >
                <Typography variant="subtitle2" color="text.secondary">
                    파일 업로드 히스토리가 없습니다.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    파일을 업로드하면 이곳에서 최근 기록을 확인할 수 있습니다.
                </Typography>
            </Paper>
        );
    }

    const handleToggle = (key) => {
        setExpandedKey((prev) => (prev === key ? null : key));
    };

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: '1px solid #e3e7f0',
                backgroundColor: '#fff',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    파일 업로드 히스토리
                </Typography>
            </Box>
            <Divider />
            <Stack spacing={0.5} sx={{ maxHeight: 360, overflowY: 'auto' }}>
                {entries.map((entry) => {
                    const isExpanded = expandedKey === entry.key;
                    return (
                        <Box key={entry.key}>
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: '#f4f6fb' },
                                }}
                                onClick={() => handleToggle(entry.key)}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2,
                                        backgroundColor: '#ede7f6',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#5e35b1',
                                        flexShrink: 0,
                                    }}
                                >
                                    <DescriptionRoundedIcon fontSize="small" />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                        {entry.fileName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {entry.recordsCount}건 · {(entry.geocodingType || '').toUpperCase()} / {entry.coordSystem || ''}
                                    </Typography>
                                    {entry.timestamp && (
                                        <Typography variant="caption" color="text.disabled" noWrap>
                                            {formatTimestamp(entry.timestamp)}
                                        </Typography>
                                    )}
                                </Box>
                                <IconButton size="small">
                                    <ArrowForwardIosRoundedIcon
                                        fontSize="inherit"
                                        sx={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                                    />
                                </IconButton>
                            </Box>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ px: 3, pb: 1.5 }}>
                                    <Stack spacing={1}>
                                        <Box
                                            sx={{
                                                border: '1px solid #e4e6f1',
                                                borderRadius: 2,
                                                p: 1.25,
                                                bgcolor: '#f8f9ff',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.4,
                                            }}
                                        >
                                            {[
                                                { label: '파일명', value: entry.fileName },
                                                { label: '총 건수', value: `${entry.recordsCount}건` },
                                                { label: '지오코딩 유형', value: entry.geocodingType || '-' },
                                                { label: '좌표계', value: entry.coordSystem || '-' },
                                                {
                                                    label: '업로드 시각',
                                                    value: entry.timestamp ? formatTimestamp(entry.timestamp) : '-',
                                                },
                                            ].map((row) => (
                                                <Box
                                                    key={`${entry.key}-${row.label}`}
                                                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                                                >
                                                    <Typography
                                                        component="span"
                                                        variant="caption"
                                                        sx={{ color: '#6b7280', minWidth: 72 }}
                                                    >
                                                        {row.label}
                                                    </Typography>
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        sx={{ color: '#1f2933', fontWeight: 600 }}
                                                    >
                                                        {row.value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                        {entry.sampleRecord && (
                                            <Typography
                                                variant="body2"
                                                color="primary"
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => onSelectRecord?.(entry.sampleRecord)}
                                            >
                                                샘플 결과 보기 (지도 이동)
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                                <Divider />
                            </Collapse>
                        </Box>
                    );
                })}
            </Stack>
        </Paper>
    );
};

FileUploadHistoryList.propTypes = {
    entries: PropTypes.array,
    onSelectRecord: PropTypes.func,
};

export default FileUploadHistoryList;
